import React, { createContext, useContext } from 'react';

/**
 * Context that abstracts external app dependencies (Redux, router, etc.)
 * so the UserIdentity component can be used in any React app.
 *
 * Center PWA provides a Redux-backed implementation.
 * Other apps (A2UI prototype, etc.) provide their own.
 */
export interface UserIdentityContextValue {
  /** Currently active user ID */
  userId: string | null;
  /** B2C subject claim (stable cross-environment identity) */
  identityId: string | null;
  /** Display name from token claims */
  displayName: string | null;
  /** Identity data (identity + users array) from Graph Service */
  identity: any | null;
  /** Available metaspaces (for settings navigation) */
  metaspaces: any[] | null;
  /** Called when the user selects a different identity/user */
  setUserId: (userId: string) => void;
  /** Called when the user marks a user as favorite/default */
  setFavoriteUser: (userId: string) => void;
  /** Navigate to a path (replaces useNavigate from react-router) */
  navigate: (path: string) => void;
}

const UserIdentityContext = createContext<UserIdentityContextValue | null>(null);

export function UserIdentityProvider({
  value,
  children,
}: {
  value: UserIdentityContextValue;
  children: React.ReactNode;
}) {
  return (
    <UserIdentityContext.Provider value={value}>
      {children}
    </UserIdentityContext.Provider>
  );
}

export function useUserIdentityContext(): UserIdentityContextValue {
  const ctx = useContext(UserIdentityContext);
  if (!ctx) {
    throw new Error(
      'useUserIdentityContext must be used within a UserIdentityProvider. ' +
        'Wrap your app with <UserIdentityProvider> and provide userId, identity, metaspaces, setUserId, setFavoriteUser, and navigate.'
    );
  }
  return ctx;
}
